import { checkInValidAnswer, redirectTo404Page } from "../shared/AppFormHelper";
import { ControlType } from "../enums/ControlType";
import { SetupQuestion } from "../enums/SetupQuestionData";
import {
    IAnswerProps,
    IAppFormQuestion,
    IApplicantInfo,
    IPageInfo
} from "../interfaces/IAppFormData";
import { IAppFormSelectors } from "./useAppFormState";
import { getResponses, saveResponses } from "../api/ApiAppForm";
import { useHistory } from "react-router-dom";
import { PageType } from "../enums/PageType";
import { FormEvent, useCallback, useContext, useRef } from "react";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import useAFApplication from "./useAFApplication";
import { IViewContext, LoadExtent, ViewContext } from "../providers/ViewStateProvider";
import { unstable_batchedUpdates } from "react-dom";


interface IUseAFForm {
    checkForm: (answerList: Array<IAnswerProps>) => boolean;
    getRedirectionUrl: (pageType: PageType, applicationId: number, applicantToken: string, applicantRef: string) => string;
    getCurrentPage: () => IPageInfo;
    loadFormPage: (updateLoadedFields: (responses: any) => void, resetForm: () => void) => void;
    updateApplicant: (applicant: IApplicantInfo) => void;
    navigateForward: (currentApplicant: IApplicantInfo) => void;
    navigateBackward: (currentApplicant: IApplicantInfo) => void;
    setupPageNavigation: (params: string, applicantList: IApplicantInfo[], applicantParams: string, forwardNav: boolean) => string;
    setupPageDefaultValue: (questionId: number, info: IApplicantInfo) => string;
    setupPageUpdateValue: (
        params: string,
        answerParams: Array<IAnswerProps>
    ) => IApplicantInfo;
    filterFirstnameLabel: (question: IAppFormQuestion, info: IApplicantInfo) => string;
    filterButtonLabel: (question: IAppFormQuestion, answer: string) => string;
    filterAddressLabel: (
        label: string,
        info: IApplicantInfo,
        isMoreApplicant: boolean
    ) => string;
    buttonVisibility: (controlType: number, answer: string) => boolean;
    checkValidApplication: (applicationId: string, selectors?: IAppFormSelectors) => void;
    handleFieldChange: (event: FormEvent) => void;
    updateMultipleFields: (fieldSet: any) => void;
    saveFormChanges: (onSaved: () => void, onBypassed: () => void) => void;
}

interface IUseAFFormParams extends IPageInfo {
    // Returns true if the field should be in the dataset sent to the server; false to exclude. If null, all fields are included in the dataset.
    filterFormField?: (fieldName: string) => boolean;
}


function checkForm(answerList): boolean {
    return answerList.reduce((acc, item) => {
        if (checkInValidAnswer(item?.answer)) {
            acc = false;
        }
        return acc;
    }, true);
}


function getRedirectionUrl(pageType: PageType, applicationId: number, applicantToken: string, applicantRef: string): string {
    switch (pageType) {
        case PageType.Personal:
            return `/app-form/setup/personal/${applicantRef}`;
        case PageType.Contact:
            return `/app-form/setup/contact/${applicantRef}`;
        case PageType.AppForm:
            return `/app-form/details/${applicantToken}`;
        case PageType.Declarations:
            return `/app-form/details/${applicantToken}/declarations`;
        case PageType.PolicyOwnership:
            return `/app-form/submit/${applicationId}/ownership`;
        case PageType.Payment:
            return `/app-form/submit/${applicationId}/payment`;
        case PageType.Address:
            return `/app-form/submit/${applicationId}/${applicantRef}/address`;
        case PageType.Consent:
            return `/app-form/submit/${applicationId}/consent/disclosure`;
        case PageType.Signature:
            return `/app-form/submit/${applicationId}/${applicantRef}/signature`;
        case PageType.Submit:
            return `/app-form/submit/${applicationId}/ready`;
        case PageType.Done:
            return `/app-form/done/${applicationId}`;
    }

    return null;
};



// Obsolete
function setupPageNavigation(params: string, applicantList: IApplicantInfo[], applicantParams: string, forwardNav = true): string {
    // If hit, redirect to the Personal Details page
    return `/app-form/setup/personal/${applicantParams}`;
}


function setupPageDefaultValue(questionId, info): string {
    switch (questionId) {
        case SetupQuestion.INFO:
            return info?.isInfoUnchange;
        case SetupQuestion.TITLE:
            return info?.title;
        case SetupQuestion.FIRST_NAME:
            return info?.firstName;
        case SetupQuestion.MIDDLE_NAME:
            return info?.middleName;
        case SetupQuestion.LAST_NAME:
            return info?.lastName;
        case SetupQuestion.GENDER:
            return !info?.isChangeGender ? info?.gender : "";
        case SetupQuestion.SMOKER_STATUS:
            return !info?.isChangeSmoked ? `${info?.isSmoker}` : "";
        case SetupQuestion.DATEOFBIRTH:
            return info?.dateOfBirth || "";
        case SetupQuestion.EMAIL:
            return info?.email || "";
        case SetupQuestion.PHONE:
            return info?.phone || "";
        case SetupQuestion.CONTACT_TIME:
            return info?.bestContactTime || "";
        default:
            return "";
    }
}

function setupPageUpdateValue(params, answerParams): IApplicantInfo {
    const answerInfo = answerParams?.length && answerParams[0];
    switch (params) {
        case "info":
            return {
                isInfoUnchange: answerInfo?.answer
            };
        case "dateofbirth":
            return {
                dateOfBirth: answerInfo?.answer
            };
        case "gender":
            return {
                gender: answerInfo?.answer,
                isChangeGender: false
            };
        case "smoked":
            return {
                isSmoker: answerInfo?.answer,
                isChangeSmoked: false
            };
        case "personal":
            return {
                title:
                    answerParams.find((i) => i.questionId === SetupQuestion.TITLE)
                        ?.answer || "",
                firstName:
                    answerParams.find((i) => i.questionId === SetupQuestion.FIRST_NAME)
                        ?.answer || "",
                middleName:
                    answerParams.find((i) => i.questionId === SetupQuestion.MIDDLE_NAME)
                        ?.answer || "",
                lastName:
                    answerParams.find((i) => i.questionId === SetupQuestion.LAST_NAME)
                        ?.answer || ""
            };
        case "contact":
            return {
                email:
                    answerParams.find((i) => i.questionId === SetupQuestion.EMAIL)
                        ?.answer || "",
                phone:
                    answerParams.find((i) => i.questionId === SetupQuestion.PHONE)
                        ?.answer || "",
                bestContactTime:
                    answerParams.find((i) => i.questionId === SetupQuestion.CONTACT_TIME)
                        ?.answer || ""
            };
        default:
            return {};
    }
}

function filterFirstnameLabel(question, info: IApplicantInfo): string {
    const applicantFirstname = info?.firstName;
    switch (question?.id) {
        case 1:
            const smoker = info?.isSmoker ? "smoker" : "non-smoker";
            if (info?.isChild) {
                return `Is ${info?.label} a ${getGenderString(info?.gender)} child?`;
            } else {
                return `Is ${info?.label} a ${getGenderString(info?.gender)}, ${info?.age} year old ${smoker}?`;
            }
        case 2:
        case 3:
        case 6:
        case 7:
            return info?.label
                ? question?.questionLabel.replace("applicant", info?.label)
                : question?.questionLabel;
        case 8:
        case 9:
            return applicantFirstname
                ? question?.questionLabel.replace("firstname", applicantFirstname)
                : question?.questionLabel;
        case 5:
            return applicantFirstname
                ? question?.questionLabel.replace("firstname", applicantFirstname)
                : question?.questionLabel;
        case 11:
        case 12:
        case 13:
            return applicantFirstname
                ? question?.questionLabel.replace("firstname", applicantFirstname)
                : question?.questionLabel;
        default:
            return question?.questionLabel;
    }
}

function filterButtonLabel(question, answer): string {
    if (!question?.id) return "Next";

    switch (question.id) {
        case 8:
            return !answer ? "Skip" : "Next";
        default:
            return "Next";
    }
}

function filterAddressLabel(label, info, isMoreApplicant): string {
    if (!isMoreApplicant) {
        return label.replace("[firstName]", `your`);
    }
    return info?.firstName
        ? label.replace("[firstName]", `${info?.firstName}'s`)
        : label;
}

function buttonVisibility(controlType, answer): boolean {
    switch (controlType) {
        case ControlType.RADIOBUTTON:
            const buttonStatus = !!String(answer).trim() || false;
            return buttonStatus;
        default:
            return true;
    }
}


const checkValidApplication = (applicationId, selectors) => {
    if (String(selectors.getApplicationId()) !== applicationId) {
        redirectTo404Page();
    }
};

const getGenderString = (gender: number) => {
    switch (Number(gender)) {
        case 1:
            return "male";
        case 2:
            return "female";
        default:
            return "unknown";
    }
}


export function useAFForm(pageConfig: IUseAFFormParams): IUseAFForm {
    const { actions, selectors } = useContext(AppFormControllerContext);
    const { startLoading, stopLoading, setError, clearError } = useContext<IViewContext>(ViewContext);
    const pendingValuesRef = useRef<Record<string, string>>({});    // This is only for tracking field saves; don't trigger re-renders
    const history = useHistory();
    const quoteGroupId = selectors.getQuoteGroupId();
    const applicationId = selectors.getApplicationId();

    var clientNumber = pageConfig.currentClientNumber;
    var currentPage = pageConfig.pageType;
    var filterFormField = pageConfig.filterFormField;

    const createAppAndContinue = useCallback((): void => {
        const groupId = selectors.getQuoteGroupId();
        const providerId = selectors.getInsurerId();
        const applicationId = selectors.getApplicationId();
        const { createApp } = useAFApplication();

        clearError();
        startLoading(LoadExtent.Panel);
        createApp(
            groupId,
            applicationId,
            providerId
        ).then((res) => {
            const applicants = res?.applicants;
            if (!applicants || !applicants?.length) return;
            unstable_batchedUpdates(() => {     // Continuations don't batch state updates in React below v18
                actions.setApplicationId(res.applicationId);
                actions.setApplicationToken(res.applicationToken);
                actions.setApplicantList(res?.applicants);
                //clientNumber = 1;     // Shouldn't be needed, as we're immediately navigating to the next page
            });
            history.push(`/app-form/details/${applicants[0].applicantToken}`);
            stopLoading();
        }).catch((error) => {
            setError("There was a problem loading the underwriting questions.");
            stopLoading();
        });
    }, [selectors]);


    const getCurrentPage = useCallback(() => {
        return { pageType: currentPage, currentClientNumber: clientNumber };
    }, [currentPage, clientNumber]);


    // Loads the saved responses from the server when loading the current page
    const loadFormPage = useCallback((updateLoadedFields: (responses: any) => void, resetForm: () => void) => {
        clearError();
        startLoading(LoadExtent.Panel);
        getResponses(quoteGroupId, applicationId, clientNumber, currentPage)
            .then(responses => {
                updateLoadedFields(responses);
                stopLoading();
            })
            .catch(reason => {
                resetForm();
                setError(reason);
                stopLoading();
            });
    }, [quoteGroupId, applicationId, clientNumber]);

    const updateApplicant = useCallback((applicant: IApplicantInfo) => {
        const list = selectors.getApplicantList();
        const updatedApplicantsList = list.map((item) => {
            if (item.applicantId === applicant.applicantId) {
                return applicant;
            }
            return item;
        }, []);
        actions.setApplicantList(updatedApplicantsList);
    }, [selectors]);

    // Performs forward navigation based on the current page reference and current applicant, possibly creating the Sm8 records if necessary.
    const navigateForward = useCallback((currentApplicant: IApplicantInfo): void => {
        var shouldCreateExternalApp = false;
        var currentClientNumber = currentApplicant.clientNumber;
        var nextPage: PageType;
        var isLastApplicant: boolean = selectors.isFinalApplicant(currentClientNumber);

        switch (currentPage) {
            case PageType.Personal:
                {
                    if (currentApplicant?.isChild) {
                        // Children should not have contact details entered
                        if (isLastApplicant) {
                            shouldCreateExternalApp = true;
                            nextPage = undefined;
                        }
                        else {
                            nextPage = PageType.Personal;
                            currentClientNumber++;
                        }
                    }
                    else {
                        nextPage = PageType.Contact;
                    }
                    break;
                }
            case PageType.Contact:
                {
                    // Assumed to be an adult
                    if (isLastApplicant) {
                        shouldCreateExternalApp = true;
                        nextPage = undefined;
                    }
                    else {
                        nextPage = PageType.Personal;
                        currentClientNumber++;
                    }
                    break;
                }
            default:
                return;
        }

        if (shouldCreateExternalApp) {
            if (!currentApplicant.applicantToken) {
                createAppAndContinue();
            }
            else {
                // Simply redirect to the start of the Sm8 questions, for the first applicant.
                nextPage = PageType.AppForm;
                currentClientNumber = 1;
            }
        }
        if (nextPage) {
            var targetApplicant = selectors.getApplicantList()[currentClientNumber - 1];
            history.push(getRedirectionUrl(nextPage, targetApplicant.applicationId, targetApplicant.applicantToken, `applicant${currentClientNumber}`));
        }
    }, [currentPage, selectors]);


    const navigateBackward = useCallback((currentApplicant: IApplicantInfo): void => {
        var nextPage: PageType;
        var currentClientNumber = currentApplicant.clientNumber;

        switch (currentPage) {
            case PageType.Personal:
                if (currentClientNumber <= 1) return;  // Can't navigate back from the start

                currentClientNumber--;     // Navigate to previous applicant
                var prevApplicant = selectors.getApplicantList()[currentClientNumber - 1];
                if (prevApplicant.isChild) {
                    nextPage = PageType.Personal;
                }
                else {
                    nextPage = PageType.Contact;
                }
                break;
            case PageType.Contact:
                // Assumed to be an adult
                nextPage = PageType.Personal;
                break;
            case PageType.AppForm:
                if (currentClientNumber <= 1) { //this is the first applicant
                    const applicantList = selectors.getApplicantList();
                    currentClientNumber = applicantList.length;
                    const lastApplicant = applicantList[currentClientNumber - 1];

                    if (lastApplicant.isChild) {
                        nextPage = PageType.Personal;
                    }
                    else {
                        nextPage = PageType.Contact;
                    }
                } else { //Load form for previous applicant.
                    currentClientNumber--;
                    nextPage = PageType.AppForm;
                }
                break;
            default:
                return;
        }

        if (nextPage) {
            var targetApplicant = selectors.getApplicantList()[currentClientNumber - 1];
            history.push(getRedirectionUrl(nextPage, targetApplicant.applicationId, targetApplicant.applicantToken, `applicant${currentClientNumber}`));
        }
    }, [currentPage, selectors]);


    const saveFormChanges = useCallback((onSaved: () => void, onBypassed: () => void): void => {
        if (onSaved || Object.keys(pendingValuesRef.current).length > 0) {
            var savedValues = Object.assign({}, pendingValuesRef.current);
            saveResponses(quoteGroupId, applicationId, clientNumber,
                {
                    fieldResponses: savedValues,
                    isPageTransition: (onSaved) ? true : false
                })
                .then(response => {
                    if (response?.isSuccessful && response.data) {
                        // Remove properties that have just been saved; if values are different, a subsequent update is likely in progress.
                        for (var currentField in savedValues) {
                            if (pendingValuesRef.current[currentField] === savedValues[currentField]) {
                                delete pendingValuesRef.current[currentField];
                            }
                        }

                        if (onSaved) {
                            onSaved();
                        }
                    }
                    else if (onBypassed) {
                        onBypassed();
                    }
                })
                .catch(reason => {
                    setError(reason);
                    if (onBypassed) {
                        onBypassed();
                    }
                });
        }
        else if (onBypassed) {
            onBypassed();
        }
    }, [quoteGroupId, applicationId, clientNumber]);

    const updateMultipleFields = useCallback((fieldSet: any): void => {
        if (!fieldSet) return;
        for (var currentField in fieldSet) {
            if (filterFormField && !filterFormField(currentField)) continue;

            pendingValuesRef.current[currentField] = fieldSet[currentField];
        }
    }, []);

    const handleFieldChange = useCallback((event: FormEvent): void => {
        var element = (event.target) as HTMLInputElement;
        var field: string = element.name;

        if (filterFormField && !filterFormField(field)) return;

        if (element.type === "checkbox") {
            pendingValuesRef.current[field] = element.checked.toString();
        } else {
            pendingValuesRef.current[field] = element.value;
        }

    }, []);

    return {
        checkForm,
        getRedirectionUrl,
        getCurrentPage,
        loadFormPage,
        updateApplicant,
        navigateForward,
        navigateBackward,
        setupPageNavigation,
        setupPageDefaultValue,
        setupPageUpdateValue,
        filterFirstnameLabel,
        filterButtonLabel,
        filterAddressLabel,
        buttonVisibility,
        checkValidApplication,
        handleFieldChange,
        updateMultipleFields,
        saveFormChanges
    };
};

